import React, { useEffect, useRef, useState } from 'react'
import { CirclePlusIcon } from '@fluentui/react-icons-mdl2'
import { Input } from 'antd'
import { useGeneralContext } from 'contexts/GeneralContext'
import { useTranslation } from 'lib/i18n'
import { connectSearchBox } from 'react-instantsearch-dom'
import { useDebounce } from 'use-debounce/lib'

import useRouter from 'hooks/useRouter'
import useWindowScroll from 'hooks/useWindowScroll'

import styles from './SearchInput.module.css'

const SEARCH_DEBOUNCE_TIME = 500

type SearchInputProps = {
  currentRefinement: string
  refine: (refinement: string) => void
}

const SearchInput: React.FC<SearchInputProps> = ({
  currentRefinement,
  refine,
}) => {
  const router = useRouter()
  const { t } = useTranslation()
  const searchInputRef = useRef(null)
  const { searchAutoFocus, setSearchAutoFocus } = useGeneralContext()
  const [searchTerm, setSearchTerm] = useState(currentRefinement)
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIME)
  const windowScroll = useWindowScroll()

  useEffect(() => {
    refine(debouncedSearchTerm)

    windowScroll(0, 0)
  }, [debouncedSearchTerm, refine, windowScroll])

  useEffect(() => {
    if (currentRefinement !== searchTerm) {
      setSearchTerm(currentRefinement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRefinement])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value)
  }

  const handleFocus = () => {
    setTimeout(() => setSearchAutoFocus(false), 0)
  }

  const clearSearch = () => {
    setSearchTerm('')
    searchInputRef.current.focus()
  }

  const ClearButton = () => (
    <div className={styles.clearButtonContainer}>
      <div
        className={styles.clearButton}
        onClick={() => clearSearch()}
        role="button"
        tabIndex={0}
      >
        <CirclePlusIcon />
      </div>
    </div>
  )

  return (
    <form
      id="searchInputWrapper"
      className={styles.wrapper}
      onSubmit={(e) => {
        e.preventDefault()
      }}
    >
      <Input.Search
        className={styles.searchInput}
        autoComplete="off"
        size="large"
        name="q"
        placeholder={t(
          'searchInput.placeholder2',
          'Find a celebrity or category',
        )}
        onChange={handleChange}
        onFocus={handleFocus}
        value={searchTerm}
        autoFocus={router.pathname.includes('/search') || searchAutoFocus}
        ref={searchInputRef}
        data-cy="searchInput"
      />
      {!!searchTerm.length && <ClearButton />}
    </form>
  )
}

export default connectSearchBox(SearchInput)
